import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function DeleteHeaderActionDialog({
  onSubmit,
  open,
  setOpen,
  selectedAttribute,
  setSelectedAttribute
}: {
  onSubmit: (confirmLabel: string) => void;
  open: boolean;
  setOpen: (show: boolean) => void;
  selectedAttribute: any;
  setSelectedAttribute: (attribute: any) => void;
}) {
  const [confirmLabel, setConfirmLabel] = useState("");
  const [touched, setTouched] = useState(false);

  // The label the user must type — the column header they clicked
  const expectedLabel: string =
    selectedAttribute?.columns?.find(
      (c: any) => c.index === selectedAttribute?.index
    )?.label ||
    selectedAttribute?.name ||
    "";

  const isMatch = confirmLabel === expectedLabel;
  const showError = touched && !isMatch;

  // Workaround for Radix UI bug: pointer-events stuck on body after dialog close
  useEffect(() => {
    if (!open) {
      setConfirmLabel("");
      setTouched(false);
      const timer = setTimeout(() => {
        document.body.style.pointerEvents = "";
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleClose = () => {
    setOpen(false);
    setSelectedAttribute(null);
  };

  const handleDelete = () => {
    setTouched(true);
    if (!isMatch) return;
    onSubmit(confirmLabel);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setTimeout(() => {
        document.body.style.pointerEvents = "";
      }, 100);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="aero:sm:max-w-[500px]"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          document.body.style.pointerEvents = "";
        }}
      >
        <DialogHeader>
          <DialogTitle className="aero:font-bold aero:text-xl aero:leading-9 aero:text-foreground">
            Delete Attribute
          </DialogTitle>
        </DialogHeader>

        <div className="aero:flex aero:flex-col aero:gap-6 aero:py-4">
          {/* Warning Section */}
          <div className="aero:flex aero:items-start aero:gap-3 aero:p-4 aero:rounded-lg aero:bg-red-50 aero:border aero:border-red-200">
            <AlertTriangle className="aero:w-5 aero:h-5 aero:text-red-600 aero:mt-0.5 aero:flex-shrink-0" />
            <div className="aero:flex aero:flex-col aero:gap-1">
              <p className="aero:text-sm aero:font-semibold aero:text-red-900">
                Warning: This action cannot be undone
              </p>
              <p className="aero:text-sm aero:text-red-700">
                All data associated with this attribute will be permanently
                removed.
              </p>
            </div>
          </div>

          {/* Confirmation Input */}
          <div className="aero:flex aero:flex-col aero:gap-3">
            <p className="aero:text-sm aero:text-neutral-600">
              Type{" "}
              <span className="aero:font-semibold aero:text-neutral-900">
                {expectedLabel}
              </span>{" "}
              to confirm deletion:
            </p>
            <Input
              value={confirmLabel}
              onChange={(e) => {
                setConfirmLabel(e.target.value);
                setTouched(true);
              }}
              placeholder={expectedLabel}
              className={
                showError
                  ? "aero:border-red-500 aero:focus-visible:ring-red-500"
                  : ""
              }
              autoComplete="off"
            />
            {showError && (
              <p className="aero:text-xs aero:text-red-600">
                The name does not match. Please type exactly:{" "}
                <span className="aero:font-semibold">{expectedLabel}</span>
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <div className="aero:flex aero:items-center aero:gap-3 aero:w-full">
            <Button
              type="button"
              intent="neutral"
              design="outline"
              className="aero:flex-1"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              design="solid"
              intent="destructive"
              className="aero:flex-1"
              disabled={touched && !isMatch}
              onClick={handleDelete}
            >
              Delete Attribute
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
